import { MARKETPLACE_IS_FREE } from "@/lib/marketplacePolicy";

/**
 * Compute the destination for a role-based CTA click (Vender / Comprar / Recibir cargas).
 *
 * @param {object} params
 * @param {"productor"|"comprador"|"transportista"} params.role - Role required for this action.
 * @param {string} params.redirect - Internal path to land on after auth (or as the target for authed correct role).
 * @param {object|null} params.user - Current auth user (may be null when unauthenticated).
 * @param {boolean} [params.allowGuest=false] - If true, unauthenticated users are sent to `redirect` directly (used by "Comprar").
 * @param {string} [params.action] - Optional intent tag preserved through the auth flow (e.g. "guardar", "contactar").
 * @returns {{action: "go"|"auth"|"wrong-role", to?: string, currentRole?: string, requiredRole?: string}}
 */
export function resolveRoleCta({ role, redirect, user, allowGuest = false, action }) {
    // Not logged in
    if (!user) {
        if (allowGuest) return { action: "go", to: redirect };
        const to = buildAuthPath({ mode: "register", role, redirect, action });
        return { action: "auth", to };
    }
    // Logged in with a compatible role
    const compatible = (user.roles || [user.role]).some((current) => isRoleCompatible(current, role));
    if (compatible) return { action: "go", to: redirect };
    // Admin can always go through (business decision: never show wrong-role modal to admins).
    if (user.role === "admin") return { action: "go", to: redirect };
    // Logged in with wrong role
    return { action: "wrong-role", currentRole: user.role, requiredRole: role };
}

// "comprador" and "exportador" share the buyer surface.
export function isRoleCompatible(userRole, requiredRole) {
    if (userRole === requiredRole) return true;
    if (MARKETPLACE_IS_FREE && ["productor","comprador","transportista","exportador","proveedor"].includes(userRole) && ["productor","comprador","transportista"].includes(requiredRole)) return true;
    if (requiredRole === "comprador" && userRole === "exportador") return true;
    return false;
}

// Whitelist of allowed values for the `action` param on auth links.
// Keeps the surface small and safe (no free-form intents smuggled through).
const ALLOWED_ACTIONS = new Set([
    "publicar",
    "guardar",
    "contactar",
    "cotizar",
    "unlock",
    "solicitar",
    "aceptar-carga",
]);

/**
 * Build an internal path for the auth flow that preserves role, redirect and action.
 *
 * Never returns an external URL. `redirect` is trusted only if it starts with "/".
 *
 * @param {object} params
 * @param {"register"|"login"} [params.mode="register"] - Which auth page to link to.
 * @param {string} [params.role] - Optional role to preselect.
 * @param {string} [params.redirect] - Internal path to return to after auth (must start with "/").
 * @param {string} [params.action] - Optional whitelisted intent tag.
 */
export function buildAuthPath({ mode = "register", role, redirect, action } = {}) {
    const base = mode === "login" ? "/ingresar" : "/registro";
    const qs = new URLSearchParams();
    if (role) qs.set("role", role);
    if (redirect && typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//")) {
        qs.set("redirect", redirect);
    }
    if (action && ALLOWED_ACTIONS.has(action)) qs.set("action", action);
    const s = qs.toString();
    return s ? `${base}?${s}` : base;
}
